import dayjs, { Dayjs } from 'dayjs'

import { CommonPayNormalLabelIcon, CommonPayNormalNewLabelIcon, MissionGiftIcon } from '@local/assets/icons'
import { getLastElement } from '@local/utils'

import { MissionGroupType, MissionLabel, MissionNextStepPreview, MissionRewardSelectMethodType } from '@web/gql/graphql'
import { SSRDateTimeVar } from '@web/hooks/useNow'
import { ArrayElement, Nullish } from '@web/types'

import { ProgressStamps } from './Content/ProgressStamp/ProgressStamp'

export enum MissionCardDisplayType {
  STAMP_BOARD = 'STAMP_BOARD',
  STEPPING = 'STEPPING',
  GAUGE = 'GAUGE',
}

interface Stamp {
  proof: {
    id: string // 스탬프 증거
  }
  createdDateTime: string // 스탬프 획득 일시
}
export interface StampGroup {
  name?: Nullish<string> // 스탬프 그룹명
  image?: Nullish<string> // 스탬프 대표 이미지
  stamps: Stamp[] // 스탬프 획득 일시
}

export interface MissionCardProps {
  id: string // 미션 ID
  name: string // 미션명
  missionGroupId?: string // 미션이 속한 미션그룹 ID
  missionGroupType: MissionGroupType // 미션 그룹 타입
  description?: Nullish<string> // 미션 설명
  label?: Nullish<MissionLabel> // 미션 레이블
  guide?: Nullish<string> // 미션 추가 설명
  iconUrl?: Nullish<string> // 미션 이름 옆에 붙는 아이콘
  displayType: MissionCardDisplayType // 카드 노출 형상
  weight: number // 미션 홈 노출 우선순위
  stampCountToComplete: number // 미션 완료에 필요한 스탬프 개수
  itemCountToStamp: number // 스탬프 1개 획득에 필요한 proof 개수
  startDateTime: Dayjs // 미션 시작 시간
  endDateTime: Dayjs // 미션 종료 시간
  reward: {
    name: string // 보상 상품명
    type: 'npay' | 'profileBorder' // 보상 종류
    winnerCount?: Nullish<number> // 당첨자 수
    selectMethod: MissionRewardSelectMethodType // 미션 보상 선정 방식
    Icon: typeof MissionGiftIcon | typeof CommonPayNormalLabelIcon | typeof CommonPayNormalNewLabelIcon
    imageUrl?: string // 보상 이미지 ex) 프로필 보더 미션
  }
  stampGroups: StampGroup[] // 스탬프
  missionCardGroup?: MissionCardProps[] // 단계형 미션을 위한 미션 카드 그룹
  previewText?: Nullish<string>
  nextStepPreview?: Nullish<MissionNextStepPreview>
  complete?: {
    // 미션 완료 정보
    dateTime: string // 완료 시간
  }
  isRewarded?: Nullish<boolean> // 당첨 여부
  rewardedDateTime?: Nullish<string> // 보상 지급일
  displayStampGroupCount: number // 노출되는 스탬프 그룹 수
  isBeforeStart: boolean // 미션 시작전인지 여부
  isInfiniteMission: boolean // 무한형 미션인지 여부
  isGroupStamp: boolean // 업종/지역등 그룹형 스탬프인지 여부
}

export class MissionCard {
  // 스탬프가 찍힌 스탬프 그룹 리스트
  static getCompleteMissionStampGroups(missionCard: MissionCardProps): StampGroup[] {
    const { stampGroups, itemCountToStamp } = missionCard

    return stampGroups.filter(stampGroup => stampGroup.stamps.length >= itemCountToStamp)
  }

  // 미션 완료 여부
  static isCompleteMission(missionCard: MissionCardProps): boolean {
    const { endDateTime } = missionCard

    const SSRDateTime = SSRDateTimeVar()

    // 무한형 미션인 경우
    if (missionCard.isInfiniteMission && dayjs(SSRDateTime).isAfter(endDateTime)) {
      return this.getCompleteMissionStampGroups(missionCard).length > 0
    }

    return !!missionCard.complete
  }

  // 스탬프가 찍힌 스탬프 그룹의 완료일시
  static getStampGroupDateTime(missionCard: MissionCardProps, stampGroup: StampGroup): Dayjs {
    const { itemCountToStamp } = missionCard

    return dayjs(stampGroup.stamps[itemCountToStamp - 1].createdDateTime)
  }

  // 최초로 미션을 성공한 스탬프 그룹
  static getFirstCompleteMissionStampGroup(missionCard: MissionCardProps): Nullish<StampGroup> {
    if (!this.isCompleteMission(missionCard)) return null

    const completeMissionStampGroup = this.getCompleteMissionStampGroups(missionCard)
    const { stampCountToComplete } = missionCard

    completeMissionStampGroup.sort((a, b) =>
      this.getStampGroupDateTime(missionCard, a).diff(this.getStampGroupDateTime(missionCard, b)),
    )

    return completeMissionStampGroup[stampCountToComplete - 1]
  }

  // 미션을 성공한 일시
  static getMissionCompleteDateTime(missionCard: MissionCardProps): Nullish<Dayjs> {
    if (!this.isCompleteMission(missionCard)) return null

    // 무한형 미션인 경우
    if (missionCard.isInfiniteMission) {
      return missionCard.endDateTime.subtract(1, 'second')
    }

    if (missionCard.complete?.dateTime) {
      return dayjs(missionCard.complete.dateTime)
    }

    return null
  }

  // 해당 증거가 포함된 스탬프 그룹 반환
  static getStampGroupByProofId = (missionCard: MissionCardProps, proofId: string): Nullish<StampGroup> => {
    const { stampGroups } = missionCard

    return stampGroups.find(stampGroup => stampGroup.stamps.find(stamp => stamp.proof.id === proofId))
  }

  // 해당 증거에 의해 미션이 완료된건지 여부
  static isCompleteMissionByProofId(missionCard: MissionCardProps, proofId: string): boolean {
    const firstCompleteMissionStampGroup = this.getFirstCompleteMissionStampGroup(missionCard)

    if (this.getCompleteMissionStampGroups(missionCard).length > missionCard.stampCountToComplete) return false

    if (!firstCompleteMissionStampGroup) return false

    if (firstCompleteMissionStampGroup.stamps.length > missionCard.itemCountToStamp) return false

    return firstCompleteMissionStampGroup?.stamps[missionCard.itemCountToStamp - 1].proof.id === proofId
  }

  // 스탬프 그룹을 게이지 스탬프에 필요한 데이터로 변환
  static stampGroupToProgressStampItem = (
    stampGroup: StampGroup,
    description: string,
  ): ArrayElement<ProgressStamps> => {
    return {
      title: stampGroup.name ?? '',
      count: stampGroup.stamps.length ?? 0,
      image: stampGroup.image,
      description: description,
      createDateTime: stampGroup.stamps[stampGroup.stamps.length - 1].createdDateTime,
    }
  }

  // 미션 카드 그룹(단계형 미션)중에서 현재 단계의 Index
  static getCurrentStepIndex = (missionCard: MissionCardProps) => {
    return missionCard.missionCardGroup?.findIndex(_missionCard => _missionCard.id === missionCard.id)
  }

  // 단계형 미션용 - 누적되지 않은 스탬프 그룹
  static getUnaccumulatedStampGroups = (missionCard: MissionCardProps) => {
    const stepIndex = MissionCard.getCurrentStepIndex(missionCard) ?? 0
    const beforeMissionCard = missionCard.missionCardGroup?.[stepIndex - 1] ?? null
    const beforeMissionCardStampCountToComplete = beforeMissionCard?.stampCountToComplete ?? 0

    return missionCard.stampGroups.slice(beforeMissionCardStampCountToComplete)
  }

  static sortStampGroupByCreatedDateTime(stampGroups: StampGroup[], order: 'ASC' | 'DESC' = 'DESC') {
    return stampGroups.slice().sort((prev, next) => {
      const prevLastStamp = getLastElement(prev.stamps)
      const nextLastStamp = getLastElement(next.stamps)

      const sign = order === 'DESC' ? 1 : -1

      if (!nextLastStamp || !prevLastStamp) {
        return nextLastStamp ? sign : prevLastStamp ? -sign : 0
      }

      return nextLastStamp.createdDateTime > prevLastStamp.createdDateTime ? sign : -sign
    })
  }

  static getLastStampGroup(stampGroups: StampGroup[]): undefined | StampGroup {
    return this.sortStampGroupByCreatedDateTime(stampGroups)[0]
  }

  static getLastStamp(stampGroups: StampGroup[]): undefined | Stamp {
    return getLastElement(this.getLastStampGroup(stampGroups)?.stamps)
  }

  static sortCardsByLatestAndWeight(missionCards: MissionCardProps[]) {
    const OLD_DATE_TIME = new Date(0).toISOString()

    return missionCards.slice().sort((prev, next) => {
      const prevLastStamp = MissionCard.getLastStamp(prev.stampGroups)
      const nextLastStamp = MissionCard.getLastStamp(next.stampGroups)
      const prevLastStampedDateTime = prevLastStamp?.createdDateTime ?? OLD_DATE_TIME
      const nextLastStampedDateTime = nextLastStamp?.createdDateTime ?? OLD_DATE_TIME

      if (prevLastStamp?.proof.id !== nextLastStamp?.proof.id) {
        if (prevLastStampedDateTime > nextLastStampedDateTime) return -1

        if (nextLastStampedDateTime > prevLastStampedDateTime) return 1
      }

      if (next.weight > prev.weight) return 1

      return -1
    })
  }

  // 발급받은 스탬프 개수가 완료에 필요한 스탬프를 초과했는지
  static isOverStamp = (missionCard: MissionCardProps) => {
    return missionCard.stampGroups.length > missionCard.stampCountToComplete
  }
}
